import React, { useRef, useEffect } from 'react';
import { FiTrash } from 'react-icons/fi';
import { FiActivity } from 'react-icons/fi';
import { BsFillPaletteFill } from 'react-icons/bs';
import { TbWaveSine, TbVector, TbVectorBezierCircle, TbOvalVertical, TbPolygon, TbVectorSpline, TbLine, TbFolderOpen, TbFolder } from 'react-icons/tb';
import { parseStyleString, isValidHexColor } from '../utils/svgUtils';

// Icons for shapes
const shapeIcons = {
  path: <TbVectorSpline />,
  rect: <TbVector />,
  circle: <TbVectorBezierCircle />,
  ellipse: <TbOvalVertical />,
  polygon: <TbPolygon />,
  polyline: <FiActivity />,
  line: <TbLine />,
  gradient: <TbWaveSine style={{ color: 'linear-gradient(90deg, #facc15, #2563eb)' }} />,
  css: <BsFillPaletteFill style={{ color: '#888' }} />,
};

// Expanded element panel (color picker, delete, etc.)
function ExpandedElementPanel({ node, handleElementColorChange, handleDeleteElement, handleRectAttributeChange, handleCircleAttributeChange, handleEllipseAttributeChange, handleStrokeWidthChange }) {
  // Always use the shape's icon and label
  let label = node.name;
  let icon = shapeIcons[node.name];

  // Get fill and stroke from either attributes or style
  let styleObj = {};
  if (node.attributes?.style) {
    styleObj = typeof node.attributes.style === 'string' 
      ? parseStyleString(node.attributes.style) 
      : node.attributes.style;
  }
  const fill = node.attributes?.fill || styleObj.fill;
  const stroke = node.attributes?.stroke || styleObj.stroke;
  const strokeWidth = node.attributes['stroke-width'] || node.attributes.strokeWidth || '';

  // For rect: editable x, y, width, height
  const isRect = node.name === 'rect';
  const rectAttrs = ['x', 'y', 'width', 'height'];

  // For circle: editable cx, cy, r
  const isCircle = node.name === 'circle';
  const circleAttrs = ['cx', 'cy', 'r'];

  // For ellipse: editable cx, cy, rx, ry
  const isEllipse = node.name === 'ellipse';
  const ellipseAttrs = ['cx', 'cy', 'rx', 'ry'];

  // Elements that support stroke-width
  const supportsStrokeWidth = ['rect', 'circle', 'ellipse', 'polygon', 'polyline', 'path', 'line'].includes(node.name);

  // Determine fill swatch style
  let fillSwatchStyle = {
    background: isValidHexColor(fill) ? fill : '#eeeeee',
    transition: 'background 0.2s',
    zIndex: 0,
  };
  let fillSwatchLabel = null;
  if (typeof fill === 'string' && fill.startsWith('url(')) {
    fillSwatchStyle.background = 'linear-gradient(90deg, #facc15, #2563eb)';
    fillSwatchLabel = <span style={{ fontSize: 12, color: '#888', marginTop: 2 }}>Gradient</span>;
  } else if (typeof fill === 'string' && !fill.startsWith('#') && !fill.startsWith('rgb') && fill !== 'none') {
    fillSwatchStyle.background = '#888';
    fillSwatchLabel = <span style={{ fontSize: 12, color: '#888', marginTop: 2 }}>CSS</span>;
  }

  return (
    <div className="selected-color-panel" style={{ marginBottom: 8, marginTop: 8 }}>
      <div style={{ position: 'relative', background: '#eaf1fb', borderRadius: 12, padding: 16, minHeight: 120, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', boxShadow: '0 2px 8px rgba(37,99,235,0.06)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
          {icon}
          <span className="color-value" style={{ fontWeight: 500, fontSize: 18 }}>{label}</span>
        </div>
        <div style={{ display: 'flex', flexDirection: 'row', gap: 32, width: '100%', justifyContent: 'flex-start' }}>
          {/* Fill Picker */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 64 }}>
            <div style={{ position: 'relative', width: 48, height: 48 }}>
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  border: '1.5px solid #cbd5e1',
                  borderRadius: 10,
                  boxShadow: '0 2px 8px rgba(37,99,235,0.10)',
                  ...fillSwatchStyle,
                }}
              />
              <input
                id="fill-color-picker"
                type="color"
                value={isValidHexColor(fill) ? fill : '#eeeeee'}
                disabled={!isValidHexColor(fill)}
                onChange={e => handleElementColorChange(node.id, 'fill', e.target.value)}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  opacity: 0,
                  cursor: isValidHexColor(fill) ? 'pointer' : 'not-allowed',
                  border: 'none',
                  margin: 0,
                  padding: 0,
                  zIndex: 1,
                }}
              />
            </div>
            <span style={{ marginTop: 6, fontSize: 14, color: '#222', fontWeight: 500 }}>Fill</span>
            {!isValidHexColor(fill) && <span style={{ color: '#888', fontSize: '0.95em', marginTop: 2 }}>Non-editable</span>}
            {fillSwatchLabel}
          </div>
          {/* Stroke Picker */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 64 }}>
            <div style={{ position: 'relative', width: 48, height: 48 }}>
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  border: '1.5px solid #cbd5e1',
                  borderRadius: 10,
                  boxShadow: '0 2px 8px rgba(37,99,235,0.10)',
                  background: isValidHexColor(stroke) ? stroke : '#eeeeee',
                  transition: 'background 0.2s',
                  zIndex: 0,
                }}
              />
              <input
                id="stroke-color-picker"
                type="color"
                value={isValidHexColor(stroke) ? stroke : '#eeeeee'}
                disabled={!isValidHexColor(stroke)}
                onChange={e => handleElementColorChange(node.id, 'stroke', e.target.value)}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  opacity: 0,
                  cursor: isValidHexColor(stroke) ? 'pointer' : 'not-allowed',
                  border: 'none',
                  margin: 0,
                  padding: 0,
                  zIndex: 1,
                }}
              />
            </div>
            <span style={{ marginTop: 6, fontSize: 14, color: '#222', fontWeight: 500 }}>Stroke</span>
            {!isValidHexColor(stroke) && <span style={{ color: '#888', fontSize: '0.95em', marginTop: 2 }}>Non-editable</span>}
          </div>
        </div>
        {/* Stroke width input for all supported elements */}
        {supportsStrokeWidth && (
          <div style={{ display: 'flex', flexDirection: 'row', gap: 16, marginTop: 18 }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', minWidth: 60 }}>
              <label htmlFor={`stroke-width-input`} style={{ fontSize: 13, color: '#444', fontWeight: 500, marginBottom: 2 }}>Stroke Width</label>
              <input
                id={`stroke-width-input`}
                type="number"
                min="0"
                step="0.1"
                value={strokeWidth}
                onChange={e => handleStrokeWidthChange(e.target.value, node)}
                style={{
                  width: 56,
                  padding: '4px 6px',
                  border: '1px solid #cbd5e1',
                  borderRadius: 6,
                  fontSize: 15,
                  background: '#fff',
                }}
              />
            </div>
          </div>
        )}
        {/* Rect attribute controls */}
        {isRect && (
          <div style={{ display: 'flex', flexDirection: 'row', gap: 16, marginTop: 18 }}>
            {rectAttrs.map(attr => (
              <div key={attr} style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', minWidth: 60 }}>
                <label htmlFor={`rect-${attr}-input`} style={{ fontSize: 13, color: '#444', fontWeight: 500, marginBottom: 2 }}>{attr.toUpperCase()}</label>
                <input
                  id={`rect-${attr}-input`}
                  type="number"
                  value={node.attributes[attr] || ''}
                  onChange={e => handleRectAttributeChange(attr, e.target.value, node)}
                  style={{
                    width: 56,
                    padding: '4px 6px',
                    border: '1px solid #cbd5e1',
                    borderRadius: 6,
                    fontSize: 15,
                    background: '#fff',
                  }}
                />
              </div>
            ))}
          </div>
        )}
        {/* Circle attribute controls */}
        {isCircle && (
          <div style={{ display: 'flex', flexDirection: 'row', gap: 16, marginTop: 18 }}>
            {circleAttrs.map(attr => (
              <div key={attr} style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', minWidth: 60 }}>
                <label htmlFor={`circle-${attr}-input`} style={{ fontSize: 13, color: '#444', fontWeight: 500, marginBottom: 2 }}>{attr.toUpperCase()}</label>
                <input
                  id={`circle-${attr}-input`}
                  type="number"
                  value={node.attributes[attr] || ''}
                  onChange={e => handleCircleAttributeChange(attr, e.target.value, node)}
                  style={{
                    width: 56,
                    padding: '4px 6px',
                    border: '1px solid #cbd5e1',
                    borderRadius: 6,
                    fontSize: 15,
                    background: '#fff',
                  }}
                />
              </div>
            ))}
          </div>
        )}
        {/* Ellipse attribute controls */}
        {isEllipse && (
          <div style={{ display: 'flex', flexDirection: 'row', gap: 16, marginTop: 18 }}>
            {ellipseAttrs.map(attr => (
              <div key={attr} style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', minWidth: 60 }}>
                <label htmlFor={`ellipse-${attr}-input`} style={{ fontSize: 13, color: '#444', fontWeight: 500, marginBottom: 2 }}>{attr.toUpperCase()}</label>
                <input
                  id={`ellipse-${attr}-input`}
                  type="number"
                  value={node.attributes[attr] || ''}
                  onChange={e => handleEllipseAttributeChange(attr, e.target.value, node)}
                  style={{
                    width: 56,
                    padding: '4px 6px',
                    border: '1px solid #cbd5e1',
                    borderRadius: 6,
                    fontSize: 15,
                    background: '#fff',
                  }}
                />
              </div>
            ))}
          </div>
        )}
        {/* Delete icon in bottom right */}
        <button className="icon-button delete-button" onClick={handleDeleteElement} title="Delete element" style={{ position: 'absolute', bottom: 12, right: 12, background: 'none', border: 'none', boxShadow: 'none' }}>
          <FiTrash size={22} color="#dc2626" />
        </button>
      </div>
    </div>
  );
}

// Recursive component to render the element tree
function ElementTree({ node, selectedId, setSelectedId, setSelectedElement, hoveredId, setHoveredId, level = 0, expandedGroups, setExpandedGroups, handleElementColorChange, handleDeleteElement, handleRectAttributeChange, handleCircleAttributeChange, handleEllipseAttributeChange, handleStrokeWidthChange }) {
  if (!node) return null;
  const isGroup = node.type === 'group';
  const isSelected = selectedId === node.id;
  const isHovered = hoveredId === node.id;
  const isExpanded = expandedGroups[node.id] !== false; // default to expanded

  const selectedRef = useRef(null);

  useEffect(() => {
    if (isSelected && selectedRef.current) {
      selectedRef.current.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
  }, [isSelected]);

  const handleToggle = (e) => {
    e.stopPropagation();
    setExpandedGroups(prev => ({ ...prev, [node.id]: !isExpanded }));
  };

  const handleClick = (e) => {
    e.stopPropagation();
    setSelectedId(node.id);
    setSelectedElement(node);
  };

  // Indentation
  const indent = { marginLeft: level * 18 };

  // Icon
  let icon = null;
  if (isGroup) {
    icon = (
      <span style={{ marginRight: 6, color: '#2563eb', fontWeight: 700, display: 'flex', alignItems: 'center' }}>
        {isExpanded ? <TbFolderOpen /> : <TbFolder />}
      </span>
    );
  } else if (shapeIcons[node.name]) {
    icon = (
      <span className="shape-icon" style={{ display: 'flex', alignItems: 'center', marginRight: 6 }}>
        {shapeIcons[node.name]}
      </span>
    );
  }

  // Label
  let label = isGroup ? (node.attributes.id || 'Group') : node.name;
  if (isGroup && node.attributes.id) label = `Group (${node.attributes.id})`;

  // For groups, clicking the row toggles expansion
  const handleGroupClick = (e) => {
    e.stopPropagation();
    setExpandedGroups(prev => ({ ...prev, [node.id]: !isExpanded }));
  };

  return (
    <div>
      {isSelected && !isGroup ? (
        <div ref={selectedRef}>
          <ExpandedElementPanel
            node={node}
            handleElementColorChange={handleElementColorChange}
            handleDeleteElement={handleDeleteElement}
            handleRectAttributeChange={handleRectAttributeChange}
            handleCircleAttributeChange={handleCircleAttributeChange}
            handleEllipseAttributeChange={handleEllipseAttributeChange}
            handleStrokeWidthChange={handleStrokeWidthChange}
          />
        </div>
      ) : (
        <div
          ref={isSelected ? selectedRef : null}
          className={`color-item${isSelected ? ' selected' : ''}`}
          style={{ ...indent, cursor: 'pointer', background: isHovered ? '#f0f4ff' : undefined }}
          onClick={isGroup ? handleGroupClick : handleClick}
          onMouseEnter={() => setHoveredId(node.id)}
          onMouseLeave={() => setHoveredId(null)}
        >
          {/* Color swatch for shapes */}
          {!isGroup && (
            <div
              className="color-swatch"
              style={
                node.type === 'gradient'
                  ? { backgroundImage: 'linear-gradient(90deg, #facc15, #2563eb)' }
                  : node.type === 'css'
                  ? { background: '#888' }
                  : { background: node.color }
              }
            />
          )}
          {icon}
          <span className="color-value">{label}</span>
        </div>
      )}
      {isGroup && isExpanded && node.children && node.children.filter(Boolean).map(child => (
        <ElementTree
          key={child.id}
          node={child}
          selectedId={selectedId}
          setSelectedId={setSelectedId}
          setSelectedElement={setSelectedElement}
          hoveredId={hoveredId}
          setHoveredId={setHoveredId}
          level={level + 1}
          expandedGroups={expandedGroups}
          setExpandedGroups={setExpandedGroups}
          handleElementColorChange={handleElementColorChange}
          handleDeleteElement={handleDeleteElement}
          handleRectAttributeChange={handleRectAttributeChange}
          handleCircleAttributeChange={handleCircleAttributeChange}
          handleEllipseAttributeChange={handleEllipseAttributeChange}
          handleStrokeWidthChange={handleStrokeWidthChange}
        />
      ))}
      {!isGroup && node.children && node.children.filter(Boolean).map(child => (
        <ElementTree
          key={child.id}
          node={child}
          selectedId={selectedId}
          setSelectedId={setSelectedId}
          setSelectedElement={setSelectedElement}
          hoveredId={hoveredId}
          setHoveredId={setHoveredId}
          level={level + 1}
          expandedGroups={expandedGroups}
          setExpandedGroups={setExpandedGroups}
          handleElementColorChange={handleElementColorChange}
          handleDeleteElement={handleDeleteElement}
          handleRectAttributeChange={handleRectAttributeChange}
          handleCircleAttributeChange={handleCircleAttributeChange}
          handleEllipseAttributeChange={handleEllipseAttributeChange}
          handleStrokeWidthChange={handleStrokeWidthChange}
        />
      ))}
    </div>
  );
}

// Main ElementPanel component
export default function ElementPanel({
  elementTree,
  selectedElement,
  selectedId,
  setSelectedId,
  setSelectedElement,
  hoveredId,
  setHoveredId,
  expandedGroups,
  setExpandedGroups,
  handleElementColorChange,
  handleDeleteElement,
  handleRectAttributeChange,
  handleCircleAttributeChange,
  handleEllipseAttributeChange,
  handleStrokeWidthChange,
}) {
  if (!elementTree) return null;

  // Skip rendering the root container and directly render its children
  const elementsToRender = elementTree.name === 'root' ? elementTree.children : [elementTree];

  return (
    <div className="colors-panel">
      <h2>All Elements</h2>
      <div className="elements-scrollable">
        {elementsToRender.map(node => (
          <ElementTree
            key={node.id}
            node={node}
            selectedId={selectedId}
            setSelectedId={setSelectedId}
            setSelectedElement={setSelectedElement}
            hoveredId={hoveredId}
            setHoveredId={setHoveredId}
            expandedGroups={expandedGroups}
            setExpandedGroups={setExpandedGroups}
            handleElementColorChange={handleElementColorChange}
            handleDeleteElement={handleDeleteElement}
            handleRectAttributeChange={handleRectAttributeChange}
            handleCircleAttributeChange={handleCircleAttributeChange}
            handleEllipseAttributeChange={handleEllipseAttributeChange}
            handleStrokeWidthChange={handleStrokeWidthChange}
          />
        ))}
      </div>
    </div>
  );
} 